// The web SDK in use (firebase 12.19, @firebase/functions 0.14) appends the HTTP
// status to every callable error message: the server's "Cannot edit this
// registration" arrives as "Cannot edit this registration [403]". Two helpers
// keep that suffix out of the UI; see "Callable errors on the client" in
// docs/spec/19-infra.md.

import { tStandalone } from "../i18n";
import { CLIENT_OUTDATED_CODE } from "./callFunction";

// One trailing " [NNN]" status, nothing else.
const STATUS_SUFFIX = /\s\[\d{3}\]$/;

/**
 * The server refused this bundle as below the live compatibility level
 * (`callFunction` marks the same error `clientOutdated`). Both display helpers
 * answer it with the translated update text, so the machine code
 * "client-outdated" or a raw key never reaches the UI.
 */
function isClientOutdated(err) {
  return !!err && (err.clientOutdated === true || (!!err.details && err.details.code === CLIENT_OUTDATED_CODE));
}

/**
 * The text of a callable rejection to SHOW the user: the message without the
 * trailing HTTP-status suffix, else `fallback`.
 *
 * The suffix is stripped only for callable errors (`err.code` = "functions/...").
 * Firestore, Storage and client-side errors never carry it, so a bracketed tail
 * there is real content and stays.
 */
export function callableErrorText(err, fallback) {
  if (isClientOutdated(err)) return tStandalone("compat.clientOutdated");
  const message = err?.message;
  if (typeof message !== "string") return fallback;
  const isCallable = typeof err.code === "string" && err.code.startsWith("functions/");
  const text = isCallable ? message.replace(STATUS_SUFFIX, "") : message;
  return text || fallback;
}

/**
 * The text a callable rejection TRAVELS under: the machine code the server sent
 * in `details.code`, else the clean message, else `fallback`.
 *
 * Why not just `err.message`: with the suffix above, a modal that compares the
 * message to a bare code ("SESSION_FULL") would miss. The server therefore also
 * sends `{ code }` in `details`, which the SDK leaves untouched. The message
 * stays the fallback for servers that predate that and for errors without a code.
 */
export function callableErrorMessage(err, fallback) {
  if (isClientOutdated(err)) return tStandalone("compat.clientOutdated");
  const detailsCode = err?.details?.code;
  if (typeof detailsCode === "string" && detailsCode) return detailsCode;
  return callableErrorText(err, fallback);
}
